import {
  ReactNode, useState,
} from 'react';
import styled from 'styled-components';
import {
  SuccessIcon, InformationIcon, WarningIcon, ErrorIcon,
} from '../../Icons';
import { useStyledTheme } from '../../../styling';
import { Flex } from '../../Flex';

const AlertWrapper = styled(Flex) <{ isMarginBottom?: boolean, isMarginTop?: boolean }>`
  align-items: center;
  position: relative;
  width: 100%;
  margin-bottom: ${({ isMarginBottom, theme }) => (isMarginBottom ? theme.padding[5] : '')};
  margin-top: ${({ isMarginTop, theme }) => (isMarginTop ? theme.padding[7] : '')};
`;

const AlertBody = styled(Flex) <{ color?: string, isBoxShadow?: boolean }>`${({ theme, color, isBoxShadow }) => `
  width: 100%; 
  padding: calc(${theme.padding[2]} + ${theme.borderWidth[1]}) ${theme.padding[4]} calc(${theme.padding[2]} + ${theme.borderWidth[1]}) calc(${theme.padding[5]} + ${theme.padding[9]});
  background-color: ${color};
  font-size: ${theme.fontSize[2]};
  border-radius: ${theme.borderRadius[2]};
  align-items: center;
  color: ${theme.textPrimary};
  line-height: ${theme.fontSize[6]};
  min-height:  ${theme.alertMinHeight};
  box-sizing: border-box;
  justify-content: space-between;
  box-shadow: ${isBoxShadow ? ' 0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)' : 'none'};
`}`;

const AlertIcon = styled(Flex) <{ color?: string }>`${({ theme, color }) => `
  padding: 0 ${theme.padding[2]};
  align-items: center;
  border-top-left-radius: ${theme.borderRadius[2]};
  border-bottom-left-radius: ${theme.borderRadius[2]};
  background-color: ${color};
  position: absolute;
  height: 100%;
`}`;

const AlertIconWrapper = styled.div`
  width: ${({ theme }) => theme.fontSize[7]};
  height: ${({ theme }) => theme.fontSize[7]};
`;

const CloseIcon = styled.i` ${({ theme }) => `
  font-size: ${theme.fontSize[3]};
  color: ${theme.colours.neutral[2]};
  cursor: pointer;
  padding-left: ${theme.padding[9]};
`}`;

// eslint-disable-next-line no-shadow
export enum AlertTypeEnum {
  SUCCESS = 'Success',
  INFORMATION = 'Information',
  WARNING = 'Warning',
  ERROR = 'Error',
}

type AlertMessageType = {
  children: ReactNode,
  showClose?: boolean,
  isBoxShadow?: boolean,
  isMarginBottom?: boolean,
  isMarginTop?: boolean,
  onClose?: () => void,
  messageColor?: string,
  iconColor?: string,
}

type AlertType = {
  icon: ReactNode,
}

export function Alert({
  showClose = false, isBoxShadow = false, children, onClose, isMarginBottom = false, isMarginTop = false, icon, iconColor, messageColor,
}: AlertMessageType & AlertType) {
  const [isOpen, setIsOpen] = useState<boolean>(true);

  if (isOpen) {
    return (
      <AlertWrapper isMarginBottom={isMarginBottom} isMarginTop={isMarginTop}>
        {icon && (
          <AlertIcon color={iconColor}>
            <AlertIconWrapper>
              {icon}
            </AlertIconWrapper>
          </AlertIcon>
        )}
        <AlertBody color={messageColor} isBoxShadow={isBoxShadow}>
          {children}
          {showClose && (
            <CloseIcon
              className="mi-close"
              onClick={() => {
                setIsOpen(false);
                onClose?.();
              }}
            />
          )}
        </AlertBody>
      </AlertWrapper>
    );
  }
  return (<></>);
}

export function AlertSuccess(props: AlertMessageType) {
  const theme = useStyledTheme();
  const { children } = props;

  return (
    <Alert
      {...props}
      icon={<SuccessIcon fill={theme.colours.success[1]} />}
      iconColor={theme.colours.success[1]}
      messageColor={theme.colours.success[2]}
    >
      {children}
    </Alert>
  );
}

export function AlertError(props: AlertMessageType) {
  const theme = useStyledTheme();
  const { children } = props;

  return (
    <Alert
      {...props}
      icon={<ErrorIcon fill={theme.colours.danger[1]} />}
      iconColor={theme.colours.danger[1]}
      messageColor={theme.colours.danger[2]}
    >
      {children}
    </Alert>
  );
}

export function AlertInformation(props: AlertMessageType) {
  const theme = useStyledTheme();
  const { children } = props;

  return (
    <Alert
      {...props}
      icon={<InformationIcon fill={theme.colours.info[1]} />}
      iconColor={theme.colours.info[1]}
      messageColor={theme.colours.info[2]}
    >
      {children}
    </Alert>
  );
}

export function AlertWarning(props: AlertMessageType) {
  const theme = useStyledTheme();
  const { children } = props;

  return (
    <Alert
      {...props}
      icon={<WarningIcon fill={theme.colours.warning[1]} />}
      iconColor={theme.colours.warning[1]}
      messageColor={theme.colours.warning[2]}
    >
      {children}
    </Alert>
  );
}

export * from './AlertMessageList';
